/**
 * Exam Environment Helper Functions
 * Provides utilities to standardize question formats between different paper sources
 */

/**
 * Normalizes a question object to the format expected by ExamEnvironment
 * Handles both previous year papers and AI-generated papers
 * @param {Object} question - Raw question object from any source
 * @param {number} index - Question index for fallback ID
 * @returns {Object} Normalized question object
 */
export function normalizeQuestion(question, index = 0) {
  if (!question) return null;

  // Generate consistent ID
  const id = question.id || question.questionId || `Q${index + 1}`;
  
  // Get question text - try multiple possible properties
  const text = question.text || question.question || question.questionText || '';
  
  // Get question type with fallback
  const type = question.type || question.questionType || 'mcq';
  
  // Normalize options - handle different formats
  let options = [];
  if (question.options) {
    if (Array.isArray(question.options)) {
      // Remove letter prefixes (A), B), etc.) if present
      options = question.options.map(opt => {
        if (typeof opt === 'string') {
          return opt.replace(/^[A-Z][.)]\s*/, '').trim();
        }
        return opt;
      });
    }
  } else if (question.choices) {
    // Handle 'choices' property as alternative to 'options'
    options = Array.isArray(question.choices) ? question.choices : [];
  }

  // Get other properties with fallbacks
  const marks = question.marks || question.score || question.points || 1;
  const topic = question.topic || question.subject || question.category || '';
  const difficulty = question.difficulty || question.level || 'medium';
  const solution = question.solution || question.explanation || question.answer_explanation || '';
  const correctAnswer = question.correctAnswer || question.correct_answer || question.answer || '';

  return {
    id,
    text,
    type,
    options,
    marks,
    topic,
    difficulty,
    solution,
    correctAnswer,
    // Keep original data for debugging
    _original: question
  };
}

/**
 * Normalizes an array of questions
 * @param {Array} questions - Array of raw question objects
 * @returns {Array} Array of normalized question objects
 */
export function normalizeQuestions(questions) {
  if (!Array.isArray(questions)) return [];
  
  return questions
    .map((q, index) => normalizeQuestion(q, index))
    .filter(q => q && q.text); // Filter out invalid questions
}

/**
 * Normalizes exam info from different paper sources
 * @param {Object} paper - Paper object from any source
 * @returns {Object} Normalized exam info
 */
export function normalizeExamInfo(paper) {
  if (!paper) return {};

  return {
    title: paper.title || paper.name || paper.paperName || 'Untitled Exam',
    description: paper.description || paper.subtitle || '',
    year: paper.year || paper.examYear || 'Unknown',
    subject: paper.subject || paper.category || paper.examId || '',
    duration: paper.duration || paper.durationMinutes || 60,
    totalMarks: paper.totalMarks || paper.maxMarks || 0,
    questionCount: paper.questionCount || paper.totalQuestions || (paper.questions ? paper.questions.length : 0)
  };
}

/**
 * Prepares exam data for ExamEnvironment component
 * Works with both previous papers and generated papers
 * @param {Object} paper - Paper object from any source
 * @param {Object} candidate - Candidate info (optional)
 * @returns {Object} Props ready for ExamEnvironment
 */
export function prepareExamData(paper, candidate = { name: 'Student' }) {
  if (!paper) {
    throw new Error('Paper data is required');
  }

  const examInfo = normalizeExamInfo(paper);
  const questions = normalizeQuestions(paper.questions || []);
  
  // Calculate timer - prefer duration in minutes, convert to seconds
  let initialTimer = 3600; // Default 1 hour
  if (paper.duration) {
    initialTimer = paper.duration * 60;
  } else if (paper.durationMinutes) {
    initialTimer = paper.durationMinutes * 60;
  } else if (questions.length > 0) {
    // Fallback: 2 minutes per question
    initialTimer = questions.length * 120;
  }

  return {
    examInfo,
    candidate,
    questions,
    initialTimer
  };
}

/**
 * Detects the source type of a paper for debugging
 * @param {Object} paper - Paper object
 * @returns {string} Source type identifier
 */
export function detectPaperSource(paper) {
  if (!paper) return 'unknown';
  
  if (paper.sourceType === 'selected_papers_genai' || paper.generationType) {
    return 'ai_generated';
  }
  
  if (paper.examId && paper.year && !paper.generationType) {
    return 'previous_paper';
  }
  
  return 'unknown';
}